/**
 * The Glass — the pulse pane (wave glass-5, #802; THE GLASS ruled #786).
 *
 * A sub-route of The Glass: drift rendered — the last live-drift run and what
 * moved — from the committed drift artifacts (engineering/contract/drift/
 * <date>.yaml, written by live-drift-report.py --emit; the --emit design ruled
 * by Chris 2026-08-31, #802). This pane reads NO live system at render time —
 * the live read happens wherever --emit is run; each artifact's own run_at is
 * the freshness it wears.
 *
 * The glass constitution (design-786 §2.10) governs: render canon, never
 * author state — no sentence on this page describes how things stand except
 * canon values carried verbatim; pane prose is mechanical labels only
 * (counts, dates, field names, renders-<source> lines). Freshness is
 * DISPLAYED, never auto-judged: run_at dates are the artifacts' own — no age
 * threshold, no color verdict, no flag. moved lines render verbatim; a
 * moved: [] artifact renders as its dated fact (0 lines), never as a "clean"
 * verdict. The report stays report-only, never blocking — a human rules on
 * live drift.
 */

import { useState } from "react";
import { GLASS_CANON, type GlassDriftArtifact } from "@command-center/shared";
import { cn } from "@/lib/utils";

export function GlassPulsePage() {
  // Filename order is chronological (date filenames sort); newest last.
  const { artifacts } = GLASS_CANON.drift;
  const newest = artifacts[artifacts.length - 1];

  return (
    <section className="mx-auto max-w-6xl p-6">
      <h1 className="mb-1 text-xl tracking-tight">The Glass — Pulse</h1>
      <p className="mb-1 text-sm text-plane-muted">
        Drift rendered — the last live-drift run and what moved, from the
        committed artifacts named below.
      </p>
      <p className="mb-5 text-xs text-plane-muted">
        renders <code>{GLASS_CANON.meta.sources.drift}&lt;date&gt;.yaml</code>{" "}
        (written by <code>engineering/contract/bin/live-drift-report.py
        --emit</code>), packaged at build time; this page
        renders them read-only. Report-only, never blocking — a human rules on
        live drift.
      </p>

      {newest !== undefined ? (
        <>
          <SectionHeading
            label="Last run"
            title={`run_at ${newest.data.run_at}`}
            source={newest.sourcePath}
          />
          <div className="rounded-md border border-border bg-card p-4">
            <ArtifactBody artifact={newest} />
          </div>
        </>
      ) : null}

      <SectionHeading
        label="All runs"
        title={`${artifacts.length} committed artifacts`}
        source={`${GLASS_CANON.meta.sources.drift}<date>.yaml`}
      />
      {[...artifacts].reverse().map((a) => (
        <ArtifactCard key={a.sourcePath} artifact={a} />
      ))}
    </section>
  );
}

function SectionHeading({
  label,
  title,
  source,
}: {
  label: string;
  title: string;
  source: string;
}) {
  return (
    <div className="mb-3 mt-10 first:mt-0">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-plane-muted">
        {label}
      </div>
      <h2 className="text-base font-semibold">{title}</h2>
      <p className="text-xs text-plane-muted">
        renders <code>{source}</code>
      </p>
    </div>
  );
}

/** One artifact's values, verbatim: run_at, the comparison-scope counts, and
 *  the moved lines exactly as the report printed them. */
function ArtifactBody({ artifact }: { artifact: GlassDriftArtifact }) {
  const { data } = artifact;
  return (
    <>
      <dl className="mb-3 space-y-1 text-xs">
        <MetaField label="run_at" value={data.run_at} />
        <MetaField
          label="airtable"
          value={`live ${data.airtable.live.tables} tables / ${data.airtable.live.fields} fields · snapshot ${data.airtable.snapshot.tables} tables / ${data.airtable.snapshot.fields} fields`}
        />
        <MetaField
          label="n8n"
          value={`live_nonarchived ${data.n8n.live_nonarchived} · roster ${data.n8n.roster}`}
        />
        {data.cloudflare !== undefined ? (
          <MetaField
            label="cloudflare"
            value={`live ${data.cloudflare.live} workers · snapshot ${data.cloudflare.snapshot}`}
          />
        ) : null}
        <MetaField label="moved" value={`${data.moved.length} lines`} />
      </dl>
      {data.moved.length > 0 ? (
        <ul className="space-y-1 text-xs" data-testid="glass-drift-moved">
          {data.moved.map((line) => (
            <li key={line}>
              <code>{line}</code>
            </li>
          ))}
        </ul>
      ) : null}
      <p className="mt-3 text-xs text-plane-muted">
        renders <code>{artifact.sourcePath}</code>
      </p>
    </>
  );
}

/** One expandable row per committed artifact, newest first. */
function ArtifactCard({ artifact: a }: { artifact: GlassDriftArtifact }) {
  const [open, setOpen] = useState(false);

  return (
    <div
      className="mb-3 rounded-md border border-border bg-card"
      data-testid={`glass-drift-${a.date}`}
    >
      <button
        type="button"
        className={cn(
          "flex w-full items-baseline gap-2 px-4 py-2.5 text-left text-sm hover:bg-muted",
          open && "bg-muted",
        )}
        onClick={() => setOpen(!open)}
      >
        <span className="font-medium">{a.date}</span>
        <code className="text-xs text-plane-muted">run_at {a.data.run_at}</code>
        <span className="text-xs text-plane-muted">
          moved: {a.data.moved.length} lines
        </span>
        <span className="ml-auto text-xs text-plane-muted">
          {open ? "collapse" : "expand"}
        </span>
      </button>
      {open ? (
        <div className="border-t border-border px-4 py-3">
          <ArtifactBody artifact={a} />
        </div>
      ) : null}
    </div>
  );
}

function MetaField({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="inline font-medium text-plane-muted">{label}: </dt>
      <dd className="inline">{value}</dd>
    </div>
  );
}
